import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import financeService from '../services/financeService';
import { userService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/permissions';
import { Link } from 'react-router-dom';
import { BarChart3, ChevronLeft, ChevronRight, Users, DollarSign, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const getMonthName = (month) => {
  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return months[month - 1] || '';
};

const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear - 2; i <= currentYear + 1; i++) {
    years.push(i);
  }
  return years;
};

const MetricCard = ({ icon: Icon, label, value, variant = 'default', subtext }) => {
  const variantStyles = {
    default: 'bg-white border-gray-100',
    primary: 'bg-gradient-to-br from-primary-500 to-primary-600 border-primary-400',
    warning: 'bg-gradient-to-br from-amber-400 to-amber-500 border-amber-400',
    success: 'bg-gradient-to-br from-green-500 to-green-600 border-green-400',
    danger: 'bg-gradient-to-br from-red-500 to-red-600 border-red-400'
  };

  const textStyles = {
    default: 'text-gray-900',
    primary: 'text-white',
    warning: 'text-white',
    success: 'text-white',
    danger: 'text-white'
  };

  const subTextStyles = {
    default: 'text-gray-500',
    primary: 'text-primary-100',
    warning: 'text-amber-100',
    success: 'text-green-100',
    danger: 'text-red-100'
  };

  return (
    <div className={`group relative rounded-2xl border-2 p-5 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${variantStyles[variant]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-sm font-medium ${subTextStyles[variant]} mb-1`}>{label}</p>
          <p className={`text-2xl font-bold tracking-tight ${textStyles[variant]}`}>{value}</p>
          {subtext && (
            <p className={`text-xs mt-1 ${subTextStyles[variant]} opacity-80`}>{subtext}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl transition-colors ${variant === 'default' ? 'bg-gray-50 group-hover:bg-primary-50' : 'bg-white/20 group-hover:bg-white/30'}`}>
          <Icon className={`w-6 h-6 ${variant === 'default' ? 'text-gray-400 group-hover:text-primary-500' : 'text-white'}`} />
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  if (status === 'Aprovado' || status === true) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="w-3.5 h-3.5" />
        Aprovado
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
      <AlertCircle className="w-3.5 h-3.5" />
      Pendente
    </span>
  );
};

const FinancePayroll = () => {
  const { user } = useAuth();
  const canView = hasPermission(user?.role, 'financial:full') || hasPermission(user?.role, 'financial:view');

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  const [filters, setFilters] = useState({ 
    year: currentYear, 
    month: currentMonth, 
    user_id: '' 
  });

  const handlePreviousMonth = () => {
    let newMonth = filters.month;
    let newYear = filters.year;
    if (newMonth === 1) { newMonth = 12; newYear -= 1; } else { newMonth -= 1; }
    setFilters({ ...filters, month: newMonth, year: newYear });
  };

  const handleNextMonth = () => {
    let newMonth = filters.month;
    let newYear = filters.year;
    if (newMonth === 12) { newMonth = 1; newYear += 1; } else { newMonth += 1; }
    setFilters({ ...filters, month: newMonth, year: newYear });
  };

  const handleCurrentMonth = () => {
    setFilters({ ...filters, month: currentMonth, year: currentYear });
  };

  const { data: usersData } = useQuery({ queryKey: ['users-mini'], queryFn: () => userService.getUsers({ limit: 200, active: true }) });
  const { data, isLoading } = useQuery({ 
    queryKey: ['finance-payroll', filters], 
    queryFn: () => financeService.getPayroll(filters), 
    keepPreviousData: true 
  });

  const users = (() => {
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    if (Array.isArray(usersData?.data)) return usersData.data;
    if (Array.isArray(usersData)) return usersData;
    return [];
  })();

  const items = Array.isArray(data?.data) ? data.data : [];

  const totais = {
    bruto: items.reduce((sum, item) => sum + (item.previsto_salary_bruto || 0), 0),
    desconto: items.reduce((sum, item) => sum + (item.advance_deduction || 0), 0),
    liquido: items.reduce((sum, item) => sum + (item.previsto_salary_liquido || 0), 0),
    aplicado: items.reduce((sum, item) => sum + (item.aplicado_salary || 0), 0)
  };

  const approvedCount = items.filter(item => item.aplicado_status).length;
  const pendingCount = items.length - approvedCount;

  if (!canView) return (
    <div className="p-6">
      <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
        <p className="text-red-800">Sem permissão para visualizar folha.</p>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Folha de Pagamento</h1>
          <p className="text-gray-500 mt-1">Competência {getMonthName(filters.month)}/{filters.year}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/financeiro" className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-colors text-sm font-medium">Dashboard</Link>
          <Link to="/financeiro/entradas" className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-colors text-sm font-medium">Entradas</Link>
          <Link to="/financeiro/saidas" className="px-4 py-2 border-2 border-gray-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 transition-colors text-sm font-medium">Saídas</Link>
          <Link
            to="/financeiro/folha/analise"
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <BarChart3 className="w-4 h-4" />
            Análise Detalhada
          </Link>
        </div>
      </div>

      {/* Date Navigation */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="bg-white rounded-2xl border-2 border-gray-100 p-2 flex items-center gap-1">
          <button onClick={handlePreviousMonth} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <select
            value={filters.month}
            onChange={(e) => setFilters({ ...filters, month: parseInt(e.target.value) })}
            className="px-2 py-1.5 bg-transparent text-gray-700 font-medium text-sm focus:outline-none"
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{getMonthName(m)}</option>
            ))}
          </select>
          <select
            value={filters.year}
            onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
            className="px-2 py-1.5 bg-transparent text-gray-700 font-medium text-sm focus:outline-none"
          >
            {getYearOptions().map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={handleNextMonth} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <button
          onClick={handleCurrentMonth}
          className="px-4 py-2 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-xl font-medium transition-colors text-sm border-2 border-primary-200"
        >
          Mês Atual
        </button>

        <div className="bg-white rounded-2xl border-2 border-gray-100 px-4 py-2">
          <select
            value={filters.user_id}
            onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
            className="bg-transparent text-gray-700 font-medium text-sm focus:outline-none min-w-[180px]"
          >
            <option value="">Todos colaboradores</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={DollarSign}
          label="Salário Bruto"
          value={`R$ ${totais.bruto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          subtext={`${items.length} colaboradores`}
        />
        <MetricCard
          icon={Clock}
          label="Adiantamentos"
          value={`R$ ${totais.desconto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          subtext={`${pendingCount} pendentes`}
          variant={totais.desconto > 0 ? 'warning' : 'default'}
        />
        <MetricCard
          icon={Users}
          label="Salário Líquido"
          value={`R$ ${totais.liquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          subtext="Previsto"
        />
        <MetricCard
          icon={CheckCircle}
          label="Aplicado"
          value={`R$ ${totais.aplicado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          subtext={`${approvedCount} aprovados`}
          variant={totais.aplicado > 0 ? 'success' : 'default'}
        />
      </div>

      {/* Legend */}
      <div className="bg-primary-50/50 border border-primary-100 rounded-2xl p-4">
        <p className="text-sm text-gray-600">
          <span className="font-medium text-primary-700">Legenda:</span>
          <span className="ml-2">•</span>
          <span className="font-medium">Salário Bruto</span>: Estimativa baseada na configuração de salário e jornadas (sem descontos).
          <span className="ml-3">•</span>
          <span className="font-medium">Adiantamentos</span>: Valor do adiantamento quinzenal aprovado.
          <span className="ml-3">•</span>
          <span className="font-medium">Salário Líquido</span>: Bruto menos adiantamento.
          <span className="ml-3">•</span>
          <span className="font-medium">Aplicado</span>: Holerites já aprovados.
        </p>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border-2 border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-primary-500 rounded-full animate-spin" />
            <p className="text-gray-500 mt-3">Carregando folha...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-gray-500 font-medium">Nenhum registro encontrado</p>
            <p className="text-gray-400 text-sm mt-1"> para o período selecionado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-primary-50/50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  <th className="px-6 py-3">Colaborador</th>
                  <th className="px-4 py-3 text-right">Bruto</th>
                  <th className="px-4 py-3 text-right">Adiantamento</th>
                  <th className="px-4 py-3 text-right">Líquido</th>
                  <th className="px-4 py-3 text-right">Aplicado</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3">Data Aprovação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {items.map((item, index) => (
                  <tr key={item.user_id || index} className="hover:bg-primary-50/30 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">{item.full_name}</span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      {item.previsto_salary_bruto > 0 ? (
                        <span className="font-medium text-gray-900">
                          R$ {item.previsto_salary_bruto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      {item.advance_deduction > 0 ? (
                        <div className="flex flex-col items-end">
                          <span className="font-medium text-red-600">
                            -R$ {item.advance_deduction.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </span>
                          {item.advance_details && (
                            <span className="text-xs text-gray-400 mt-0.5">
                              {new Date(item.advance_details.approved_at).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      {item.previsto_salary_liquido > 0 ? (
                        <span className="font-semibold text-primary-600">
                          R$ {item.previsto_salary_liquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      {item.aplicado_salary > 0 ? (
                        <span className="font-medium text-green-600">
                          R$ {item.aplicado_salary.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <StatusBadge status={item.aplicado_status} />
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {item.aplicado_at ? (
                        new Date(item.aplicado_at).toLocaleDateString('pt-BR')
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-primary-50/30">
                <tr>
                  <td className="px-6 py-4 text-sm font-bold text-gray-900">Total</td>
                  <td className="px-4 py-4 text-sm font-bold text-gray-900 text-right">
                    R$ {totais.bruto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-4 text-sm font-bold text-red-600 text-right">
                    -R$ {totais.desconto.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-4 text-sm font-bold text-primary-600 text-right">
                    R$ {totais.liquido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-4 text-sm font-bold text-green-600 text-right">
                    R$ {totais.aplicado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-4 py-4"></td>
                  <td className="px-4 py-4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancePayroll;